import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { Observable, Subscription } from "rxjs";

import { CustomerPartyDTO } from "src/app/models/Party/customerPartyDTO";
import { EmployeePartyDTO } from "src/app/models/Party/employeePartyDTO";
import { PartyAddressDTO } from "src/app/models/Party/partyAddressDTO";
import { PartyContactDTO } from "src/app/models/Party/partyContactDTO";
import { SupplierPartyDTO } from "src/app/models/Party/supplierPartyDTO";
import { PARTY_FORM_PARAMS } from "src/app/models/Party/party-form-params";
import { FormUtils } from "../../utils/formUtils";

export class BaseParty {
  //Date Format
  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  dateFormat: string;

  //Party Status List
  statusList = [
    { label: "Status.Active", value: "ACTIVE" },
    { label: "Status.Inactive", value: "INACTIVE" },
    { label: "Status.Blocked", value: "BLOCKED" },
  ];

  // Party Contacts
  contactTypes = ["email", "phone", "mobile"];

  emailRegEx =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  constructor(public fb: FormBuilder) {}

  loadTenantDateFomat(): void {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.dateFormat = data.dateFormat;
      }
    });
  }

  buildPartyForm() {
    return this.fb.group({
      [PARTY_FORM_PARAMS.statusCode]: ["ACTIVE", Validators.required],
      [PARTY_FORM_PARAMS.blockedReason]: [null],
      [PARTY_FORM_PARAMS.code]: [null],
      [PARTY_FORM_PARAMS.vatin]: [null, [Validators.required, Validators.maxLength(20), FormUtils.whitespaceBetweenValidator]],
      [PARTY_FORM_PARAMS.name]: [null, [Validators.required, Validators.maxLength(45), FormUtils.noWhitespaceValidator]],
      [PARTY_FORM_PARAMS.birthday]: [null],
      [PARTY_FORM_PARAMS.salutationCode]: [null],
      [PARTY_FORM_PARAMS.contactName]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.phone]: [null, [Validators.maxLength(45), FormUtils.whitespaceBetweenValidator]],
      [PARTY_FORM_PARAMS.email]: [null, [Validators.maxLength(45), Validators.pattern(this.emailRegEx)]],
      [PARTY_FORM_PARAMS.mobile]: [null, [Validators.maxLength(45), FormUtils.whitespaceBetweenValidator]],
      [PARTY_FORM_PARAMS.description]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.street]: [null, Validators.maxLength(100)],
      [PARTY_FORM_PARAMS.postalZipCode]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.city]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.countryCode]: [null],
      [PARTY_FORM_PARAMS.categories]: this.fb.array([]),
      [PARTY_FORM_PARAMS.properties]: this.fb.array([]),
    });
  }

  onStatusChanges(form: FormGroup): void {
    form.get(PARTY_FORM_PARAMS.statusCode).valueChanges.subscribe((selectedStatus) => {
      if (selectedStatus === "BLOCKED") {
        form.get("blockedReason").setValidators([Validators.required, Validators.maxLength(100)]);
      } else {
        form.get("blockedReason").clearValidators();
        form.get("blockedReason").updateValueAndValidity();
      }
    });
  }

  setBasePartyForm(
    form: FormGroup,
    party: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO,
    partyCode: string,
    duplication: boolean
  ) {
    form.controls[PARTY_FORM_PARAMS.statusCode].setValue(party?.statusCode ?? null);
    form.controls[PARTY_FORM_PARAMS.blockedReason].setValue(
      party?.blockedReason ?? null
    );
    form.controls[PARTY_FORM_PARAMS.code].setValue(duplication ? null : partyCode ?? null);
    form.controls[PARTY_FORM_PARAMS.name].setValue(
      duplication ? null : party?.name ?? null
    );
    form.controls[PARTY_FORM_PARAMS.birthday].setValue(
      duplication ? null : party?.birthday ?? null
    );
    form.controls[PARTY_FORM_PARAMS.vatin].setValue(
      duplication ? null : party?.vatin ?? null
    );
    form.controls[PARTY_FORM_PARAMS.salutationCode].setValue(
      duplication ? null : party?.salutationCode ?? null
    );
    form.controls[PARTY_FORM_PARAMS.contactName].setValue(
      duplication ? null : party?.additionalData?.contactName ?? null
    );
    form.controls[PARTY_FORM_PARAMS.phone].setValue(
      duplication ? null : this.getContact(PARTY_FORM_PARAMS.phone, party.contacts) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.email].setValue(
      duplication ? null : this.getContact(PARTY_FORM_PARAMS.email, party.contacts) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.mobile].setValue(
      duplication
        ? null
        : this.getContact(PARTY_FORM_PARAMS.mobile, party.contacts) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.description].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.description, party.addresses) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.street].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.street, party.addresses) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.postalZipCode].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.postalZipCode, party.addresses) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.city].setValue(
      duplication ? null : this.getAddress(PARTY_FORM_PARAMS.city, party.addresses) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.countryCode].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.countryCode, party.addresses) ?? null
    );
  }

  lockCode(form: FormGroup) {
    form.get(PARTY_FORM_PARAMS.code).disable();
  }

  clearFormControl(form: FormGroup, categoryControl: string): void {
    form.controls[categoryControl].setValue(null);
    form.markAsDirty();
  }

  getContact(contactType: string, contacts: PartyContactDTO[]): string {
    if (!contacts || !contacts.length) {
      return;
    }
    return contacts.find(
      (contact) => contact.contactMethodCode === contactType.toUpperCase()
    )?.contact;
  }

  getAddress(addressType: string, addresses: PartyAddressDTO[]): string {
    if (!addresses || !addresses.length) {
      return;
    }
    const [address] = addresses;
    return address[addressType];
  }

  setFormContacts(
    form: FormGroup,
    currentParty: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO
  ): PartyContactDTO[] {
    const contacts = this.contactTypes.reduce((acc, cur) => {
      let code = this.getContactCode(currentParty, cur.toUpperCase());
      if (!form.controls[cur].value) {
        return acc;
      }
      return [
        ...acc,
        {
          code,
          contactMethodCode: cur.toUpperCase(),
          contact: form.controls[cur].value ?? null,
          description: cur.toUpperCase(),
        },
      ];
    }, []);
    return contacts;
  }

  getContactCode(
    currentParty: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO,
    contactMethod: string
  ): number {
    if (!currentParty || !contactMethod) {
      return null;
    }
    const { contacts } = currentParty;
    if (!contacts) {
      return null;
    }
    const contactCode = contacts.find(
      (contact) => contact.contactMethodCode === contactMethod
    )?.code;
    if (!contactCode) {
      return null;
    }
    return contactCode;
  }

  setFormAddresses(
    form: FormGroup,
    currentParty: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO
  ): PartyAddressDTO[] {
    let code = this.getAddressCode(currentParty);
    return [
      {
        code,
        description: form.controls[PARTY_FORM_PARAMS.description].value ?? "",
        street: form.controls[PARTY_FORM_PARAMS.street].value ?? "",
        postalZipCode: form.controls[PARTY_FORM_PARAMS.postalZipCode].value ?? "",
        city: form.controls[PARTY_FORM_PARAMS.city].value ?? "",
        countryCode: form.controls[PARTY_FORM_PARAMS.countryCode].value ?? "",
      },
    ];
  }

  getAddressCode(currentCustomer: CustomerPartyDTO): number {
    if (!currentCustomer) {
      return null;
    }
    const { addresses } = currentCustomer;
    if (!addresses || !addresses.length) {
      return null;
    }
    const [address] = addresses;
    const { code } = address;
    return code;
  }
}
